/**
 * AppBouncer SDK - Main Entry Point
 *
 * This module provides a clean re-export surface for all SDK functionality.
 * Feature modules are organized into dedicated folders for better maintainability.
 */

// ============================================================
// Core Types (from types/ folder)
// ============================================================

export type {
  Challenge,
  BBox,
  QualityMetrics,
  FrameData,
  LivenessResult,
  VerificationResponse,
  SDKConfig,
  ClientMetadata,
} from './types';

// ============================================================
// Main Orchestration (AppBouncer)
// ============================================================

export {
  // Main class
  AppBouncer,
  createAppBouncer,

  // UI State Machine
  UIStateMachine,
  getStepIndicator,
  getErrorMessage,
  PHOTO_TIPS,
  DOCUMENT_TYPES,
  ERROR_MESSAGES,
} from './orchestration';

export type {
  AppBouncerConfig,
  AppBouncerCallbacks,
  VerificationSession,
  LivenessActionResult,
  VerificationMode,
  ScreenName,
  VerificationMethod,
  ScreenConfig,
  ScreenAction,
  UIStateConfig,
  VerificationResult,
  UIStateData,
  ErrorCode,
} from './orchestration';

// ============================================================
// Camera Module
// ============================================================

export {
  CameraManager,
  getDefaultCameraManager,
  startCamera,
  stopCamera,
  fitCanvas,
  isMobileDevice,
  getDefaultConstraints,
  trackStream,
  untrackStream,
  stopStream,
  cleanupAllCameras,
  hasActiveStreams,
  getActiveStreamCount,
  installCleanupHandlers,
  removeCleanupHandlers,
} from './camera';

export type {
  CameraConstraints,
  CameraConfig,
  CameraState,
  CameraErrorInfo,
  CameraErrorCode,
} from './camera';

// ============================================================
// Face Detection Module
// ============================================================

export {
  MediaPipeFaceDetector,
  // Math utilities
  distance,
  distance2D,
  average,
  clamp,
  // Face analysis
  computeFaceBounds,
  isFaceInsideOval,
  computeEyeEAR,
  computeEAR,
  areEyesClosed,
  computeMAR,
  computeSmileMetric,
  computeBrowRaise,
  // Head pose
  estimateYaw,
  estimateYawFromEyes,
  estimatePitch,
  estimatePitchFromMouth,
  estimateRoll,
  estimateHeadPose,
  // Quality checks
  checkFaceScale,
  checkHeadAngles,
  checkFaceCentering,
  checkEarVisibility,
  checkYawStraight,
  checkSceneQuality,
  // Lighting
  analyzeBrightness,
  analyzeLightingQuality,
  detectGlasses,
  // Misc
  mirrorLandmarks,
  sleep,
  // Constants
  LEFT_EYE_INDICES,
  RIGHT_EYE_INDICES,
  MOUTH_INDICES,
  FEATURE_INDICES,
  DEFAULT_CONFIG,
} from './face-detection';

export type {
  FaceLandmarks,
  Landmark,
  HeadPose,
  FaceBounds,
  FaceQualityCheck,
  SceneQualityResult,
  LightingQuality,
  FaceDetectionConfig,
} from './face-detection';

// ============================================================
// Liveness Module
// ============================================================

export {
  LivenessDetector,
  createLivenessDetector,
  ACTION_HINTS,
  getActionDisplayName,
  // Config
  DEFAULT_THRESHOLDS,
  LIVENESS_ACTIONS,
  ACTION_CONFIGS,
  LIGHTING_CONFIG,
  getThreshold,
  setThresholdOverride,
  clearThresholdOverrides,
  getCurrentThresholds,
  // Legacy actions
  detectBlink,
  detectHeadTurn,
  detectSmile,
  verifyActionSequence,
} from './liveness';

export type {
  LivenessAction,
  LivenessThresholds,
  ActionConfig,
  LightingConfig,
  ActionParams,
  ActionResult,
  ChallengeStep,
  LivenessConfig,
  LivenessCallbacks,
  LivenessVerifyResult,
  ActionVerification,
} from './liveness';

// ============================================================
// Age Estimation Module
// ============================================================

export {
  ONNXModels,
  ONNXInference,
  AgeEstimator,
  createAgeEstimator,
  DEFAULT_AGE_CONFIG,
  DEFAULT_OVERLAY_CONFIG,
  drawFaceOverlay,
  drawLandmarks,
  drawFaceBounds,
  normalizeBBox,
  preprocessFace,
  computeFrameHash,
  float32ArrayToBase64,
  base64ToFloat32Array,
} from './age-estimation';

export type {
  AgeModelConfig,
  AgeEstimationResult,
  AgeHistory,
  MultiEstimateConfig,
  AgeEstimateWithImage,
  MultiEstimateResult,
  OverlayConfig,
  PreprocessedFace,
} from './age-estimation';

// ============================================================
// Document Module
// ============================================================

export {
  DocumentCapture,
  createDocumentCapture,
  analyzeImageBrightness,
  isImageBlurry,
  checkDocumentQuality,
  calculateCropRegion,
  DEFAULT_DOCUMENT_CONFIG,
  SUPPORTED_COUNTRIES,
  DOCUMENT_TYPE_NAMES,
  // Overlay
  drawDocumentOverlay,
  calculateDocumentRect,
  clearDocumentOverlay,
  getRecommendedContainerAspect,
  DEFAULT_OVERLAY_CONFIG as DOCUMENT_OVERLAY_CONFIG,
  DOCUMENT_DIMENSIONS,
  PASSPORT_ZONES,
  ID_CARD_ZONES,
} from './document';

export type {
  DocumentType,
  DocumentSide,
  DocumentConfig,
  CapturedDocument,
  DocumentUploadResponse,
  DocumentVerificationResult,
  PresignedUrlData,
  PresignResponse,
  VerifyUrlsRequest,
  DocumentCaptureCallbacks,
  DocumentCaptureState,
  CountryInfo,
  CropRegion,
  DocumentQualityResult,
  DocumentOverlayConfig,
  DocumentDimensions,
} from './document';

// ============================================================
// Verification Module
// ============================================================

export {
  RequirementsService,
  RequirementsError,
  createRequirementsService,
  PollingService,
  PollingError,
  createPollingService,
  estimateTimeRemaining,
  formatDuration,
  VerificationController,
  createVerificationController,
  autoInitialize,
} from './verification';

export type {
  RequirementsData,
  RequirementsResponse,
  RequirementsServiceConfig,
  JobStatus,
  JobStatusResponse,
  VerificationStatus,
  OcrJobStatus,
  OcrStatusData,
  FaceMatchData,
  LivenessData,
  AgeData,
  VerificationStatusData,
  VerificationStatusResponse,
  PollingConfig,
  PollingCallbacks,
  PollOptions,
  PollingErrorCode,
  VerificationControllerConfig,
  URLParams,
  VerificationControllerCallbacks,
} from './verification';

// ============================================================
// Constants
// ============================================================

export {
  CAMERA,
  POLLING,
  AGE_ESTIMATION,
  FACE_DETECTION,
  DOCUMENT,
  NETWORK,
  UI,
  SESSION,
} from './constants';

export type {
  CameraConstants,
  PollingConstants,
  AgeEstimationConstants,
  FaceDetectionConstants,
  DocumentConstants,
  NetworkConstants,
  UIConstants,
  SessionConstants,
} from './constants';

// ============================================================
// Testing Utilities
// ============================================================

export * from './testing/mock-backend';

// ============================================================
// Supporting Modules
// ============================================================

// HTTP client utility
export * from './services/http-client';

// Session service
export * from './services';

// Error classes
export * from './errors';

// Internationalization (i18n)
export * from './i18n';

// Log redaction utility
export * from './utils/log-redaction';

// Loops (extracted detection loops)
export * from './loops';

// Face quality messages
export {
  FACE_MESSAGES_SHORT,
  FACE_MESSAGES_DETAILED,
  getFaceMessage,
  getFeedbackFromQuality,
  isFacePositionedCorrectly,
} from './utils/face-quality-messages';

export type {
  StatusType,
  MessageFormat,
  FaceMessageKey,
  FaceQualityFeedback,
} from './utils/face-quality-messages';

// ============================================================
// State Management
// ============================================================

export { VerificationStateManager, verificationState } from './ui/state/verification-state';

export type {
  VerificationState,
  ScreenName as StateScreenName,
  DocumentType as StateDocumentType,
  CameraFacingMode,
  LightingStatus,
  DocumentSide as StateDocumentSide,
  LivenessAction as StateLivenessAction,
  ScreenState,
  CameraState as StateCameraState,
  FaceState,
  LivenessState,
  AgeState,
  DocumentState,
  UploadsState,
  RequirementsState,
  LightingState,
  StateListener,
} from './ui/state/verification-state';

// ============================================================
// SDK Configuration
// ============================================================

export {
  DEFAULT_SDK_CONFIG,
  DEFAULT_ENDPOINTS,
  DEFAULT_TIMEOUTS,
  DEFAULT_POLLING,
  DEFAULT_LIVENESS,
  DEFAULT_CAMERA,
  createSDKConfig,
  buildUrl,
  buildUrlWithParams,
  getConfig,
  setGlobalConfig,
  resetConfig,
  getEndpointUrl,
} from './config';

export type {
  SDKConfig as ConfigSDKConfig,
  ApiEndpointsConfig,
  TimeoutsConfig,
  PollingConfig as ConfigPollingConfig,
  LivenessConfig as ConfigLivenessConfig,
  CameraConfig as ConfigCameraConfig,
  DeepPartial,
} from './config';

// ============================================================
// UI Modules
// ============================================================

// UI App coordinator
export { UIApp, initUIApp, parseUrlParams } from './ui/ui-app';
export type { ScreenId, UIAppConfig, UIAppEvents } from './ui/ui-app';

// Controllers
export {
  LightingController,
  lightingController,
  LightingConfig as UILightingConfig,
  LightingResult,
  LightingStatusListener,
} from './ui/controllers/lighting-controller';

export {
  FaceDetectionController,
  faceDetectionController,
  FaceQuality,
  FaceDetectionConfig as UIFaceDetectionConfig,
  FaceDetectionEvents,
} from './ui/controllers/face-detection-controller';

export {
  LivenessController,
  livenessController,
  ACTION_TEXT,
  ACTION_NAME_MAP,
  ActionText,
  ActionResult as UIActionResult,
  LivenessResult as UILivenessResult,
  LivenessControllerEvents,
  LivenessControllerConfig,
} from './ui/controllers/liveness-controller';

export {
  DocumentController,
  documentController,
  DocumentControllerConfig,
  DocumentControllerEvents,
} from './ui/controllers/document-controller';

// Services
export {
  BackendSyncService,
  createBackendSyncService,
  ApiConfig as BackendSyncApiConfig,
  LivenessActions,
  SelfieUploadResult,
  DocumentPollingResult,
  PollingConfig as BackendPollingConfig,
} from './ui/services/backend-sync-service';

// UI Utilities
export * from './ui/utils';

// Event Management
export {
  EventManager,
  getEventManager,
  initEventManager,
  getActionData,
  type EventHandler,
  type SupportedEventType,
  type EventManagerConfig,
} from './ui/events';

// ============================================================
// Legacy SDK (for backwards compatibility)
// ============================================================

import { MediaPipeFaceDetector } from './face-detection';
import { ONNXInference } from './age-estimation';
import { computeFrameHash, float32ArrayToBase64 } from './age-estimation';
import { verifyActionSequence } from './liveness';
import type {
  Challenge,
  LivenessResult as CoreLivenessResult,
  VerificationResponse,
  SDKConfig,
  FrameData,
  ClientMetadata,
  BBox,
} from './types';

/**
 * @deprecated Use AppBouncer instead. This legacy SDK is kept for backwards compatibility.
 */
export class AgeLivenessSDK {
  private config: SDKConfig;
  private faceDetector: MediaPipeFaceDetector;
  private onnx: ONNXInference;
  private modelVersion: string = 'client-onnx-v1';

  constructor(config: SDKConfig) {
    this.config = {
      embeddingDim: 512,
      maxFrames: 12,
      frameInterval: 150,
      ...config,
    };

    this.faceDetector = new MediaPipeFaceDetector();
    this.onnx = new ONNXInference(this.config.embeddingDim);
  }

  async initialize(): Promise<void> {
    await this.faceDetector.initialize();
    await this.onnx.initialize();

    if (this.config.embeddingModelPath) {
      await this.onnx.loadEmbeddingModel(this.config.embeddingModelPath);
    }

    if (this.config.ageModelPath) {
      await this.onnx.loadAgeModel(this.config.ageModelPath);
    }
  }

  async createChallenge(clientId: string): Promise<Challenge> {
    const response = await fetch(`${this.config.apiBaseUrl}/v1/challenge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ client_id: clientId, purpose: 'liveness' }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(`Challenge creation failed: ${error.error || response.statusText}`);
    }

    return response.json();
  }

  async runLiveness(
    challenge: Challenge,
    videoElement: HTMLVideoElement,
    opts?: {
      maxFrames?: number;
      onProgress?: (frame: number, total: number) => void;
    }
  ): Promise<CoreLivenessResult> {
    const maxFrames = opts?.maxFrames || challenge.max_frames || this.config.maxFrames || 12;
    const frameInterval = this.config.frameInterval || 150;
    const frames: FrameData[] = [];
    const frameHistory: { landmarks: number[][]; ts_ms: number }[] = [];

    for (let i = 0; i < maxFrames; i++) {
      if (opts?.onProgress) {
        opts.onProgress(i + 1, maxFrames);
      }

      const ts_ms = Date.now();
      const faceData = await this.faceDetector.detectFace(videoElement);

      if (!faceData) {
        await new Promise((resolve) => setTimeout(resolve, frameInterval));
        continue;
      }

      const embedding = await this.onnx.processFace(videoElement, faceData.bbox);

      const normalizedBBox: BBox = {
        x: faceData.bbox.x / videoElement.videoWidth,
        y: faceData.bbox.y / videoElement.videoHeight,
        w: faceData.bbox.w / videoElement.videoWidth,
        h: faceData.bbox.h / videoElement.videoHeight,
      };

      const frameHash = await computeFrameHash(faceData.landmarks, normalizedBBox, embedding);

      if (frames.length > 0 && frames[frames.length - 1].frame_hash === frameHash) {
        await new Promise((resolve) => setTimeout(resolve, frameInterval));
        continue;
      }

      const frameData: FrameData = {
        ts_ms,
        bbox: normalizedBBox,
        landmarks: faceData.landmarks,
        embedding,
        quality: {
          confidence: faceData.confidence,
          blur: 0.0,
        },
        frame_hash: frameHash,
      };

      frames.push(frameData);
      frameHistory.push({ landmarks: faceData.landmarks, ts_ms });

      for (const action of challenge.actions) {
        verifyActionSequence(action, frameHistory);
      }

      if (i < maxFrames - 1) {
        await new Promise((resolve) => setTimeout(resolve, frameInterval));
      }
    }

    if (frames.length === 0) {
      throw new Error('No valid frames captured');
    }

    const aggregatedEmbedding = new Array(this.config.embeddingDim || 512).fill(0);
    for (const frame of frames) {
      for (let i = 0; i < Math.min(frame.embedding.length, aggregatedEmbedding.length); i++) {
        aggregatedEmbedding[i] += frame.embedding[i];
      }
    }
    for (let i = 0; i < aggregatedEmbedding.length; i++) {
      aggregatedEmbedding[i] /= frames.length;
    }

    const norm = Math.sqrt(aggregatedEmbedding.reduce((sum, v) => sum + v * v, 0));
    for (let i = 0; i < aggregatedEmbedding.length; i++) {
      aggregatedEmbedding[i] /= norm;
    }

    const ageResult = await this.onnx.estimateAge(aggregatedEmbedding);

    return {
      challenge_id: challenge.challenge_id,
      frames,
      aggregated_embedding: aggregatedEmbedding,
      age_estimate: ageResult.age,
      age_confidence: ageResult.confidence,
      model_version: this.modelVersion,
    };
  }

  async submitLiveness(
    result: CoreLivenessResult,
    challenge: Challenge,
    clientMetadata?: ClientMetadata
  ): Promise<VerificationResponse> {
    const framesForSubmission = result.frames.map((frame) => ({
      ...frame,
      embedding: float32ArrayToBase64(new Float32Array(frame.embedding)),
    }));

    const payload = {
      challenge_id: result.challenge_id,
      token: challenge.token,
      client_metadata: clientMetadata || {
        ua: navigator.userAgent,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      frames: framesForSubmission,
      aggregated_embedding: float32ArrayToBase64(new Float32Array(result.aggregated_embedding)),
      age_estimate: result.age_estimate,
      age_confidence: result.age_confidence,
      model_version: result.model_version,
    };

    const response = await fetch(`${this.config.apiBaseUrl}/v1/verify_liveness`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(`Liveness verification failed: ${error.error || response.statusText}`);
    }

    return response.json();
  }

  async submitID(
    sessionToken: string,
    selfieWithID: File,
    idPhoto: File
  ): Promise<VerificationResponse> {
    const formData = new FormData();
    formData.append('session_token', sessionToken);
    formData.append('selfie_with_id', selfieWithID);
    formData.append('id_photo', idPhoto);

    const response = await fetch(`${this.config.apiBaseUrl}/v1/submit_id`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(`ID verification failed: ${error.error || response.statusText}`);
    }

    return response.json();
  }

  dispose(): void {
    this.faceDetector.dispose();
  }
}
